/**
 * @file Builds the entity profile schema, branching between the
 * disregarded-entity and the regular-entity beneficial-owner inputs.
 */
import { Alert } from "antd";
import type { RadioChangeEvent } from "antd";

import {
  ENTITY_TYPE_IRA,
  TOOLTIP_PROFILE_DRE,
  ENTITY_TYPE_TITLES_MAP,
  TOOLTIP_BENEFICIAL_OWNER_NAME,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_LLC,
  BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL,
  ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP,
} from "@components/Domain";
import { W1 } from "@components/Constants";
import { TYPE_EIN, TYPE_RADIO, TYPE_STRING } from "@components/Form";
import { MarkdownAlert, Disclaimer, Markdown } from "@components/Text";
import { getAddressSchema, getUsAddressSchema } from "@components/Address";

import RULES from "./rules";
import computeResidencyInputs from "./computeResidencyInputs";
import getProfileEntityInputsMap from "./getProfileEntityInputsMap";
import type {
  Input,
  Organization,
  UpdateSchema,
  ResidencyLabels,
  FormProfileEntity,
} from "../types";

export const TITLE_WARNING = "Warning";
const LABEL_BENEFICIAL_OWNER_NAME = "Beneficial Owner Name";
const LABEL_ENTITY_EIN = "Entity EIN";
const LABEL_HAS_EIN = "Does this entity have an EIN?";
const LABEL_TAX_FILING_STATUS = "Tax Filing Status";

export const LABEL_DISREGARDED_IS_US_PERSON = "Is the beneficial owner a U.S. Person for federal tax purposes?";
export const TOOLTIP_DISREGARDED_IS_US_PERSON = "Answer Yes if the owner is a U.S. citizen, holds a green card or otherwise files a resident return (Form 1040), or is an entity organized in the U.S. Answer No if the owner files a nonresident return (Form 1040-NR) or doesn't file U.S. taxes. Please consult your tax advisor if you are unsure.";

const TOOLTIP_DRE_BENEFICIAL_OWNER_NAME = `The beneficial owner of a disregarded
entity is the person (or entity), as shown on its income tax return, where the
disregarded entity's income or loss is reported. For example, it is the single
member of a single member LLC, or the grantor of a grantor trust. If the direct
owner of the entity is also a disregarded entity, please list the first owner
that is not disregarded for federal tax purposes. Please consult your tax
advisor if you are unsure.`;

/** Returns the warning message shown when the entity has no EIN. */
export const getNoEinAlert = (organizationEmail: string): string => `
A taxpayer identification number (TIN) is required on all returns, statements,
and other tax-related documents. We will need to collect a TIN from you in
order to proceed with your investment. If you do not have a TIN, please visit
the IRS website for instructions regarding how to obtain an ITIN for yourself
or an EIN for your entity (if applicable). Please reach out to
[${organizationEmail}](mailto:${organizationEmail}) if you have any questions.`;

export const DISCARDED_IRA_ALERT = `
You’re in the Entity/Trust path. IRAs (including SDIRAs) must be entered through
the SDIRA flow to ensure correct custodian/checkbook setup and K-1 titling.
Please return to the beginning and select SDIRA to continue.`;

const INVALID_ENTITY_TYPE_LABEL = "Invalid entity type";
export const INVALID_BENEFICIAL_OWNER_ENTITY_TYPE_LABEL = "Invalid beneficial owner entity type";

const WARNING_STYLE = { marginTop: W1, marginBottom: 0 };
const ALERT_STYLE = { marginTop: W1 };

const BENEFICIAL_OWNER_ADDRESS_LABELS = {
  addressCountry: "Beneficial Owner Country",
  streetAddress: "Beneficial Owner Address",
  addressLocality: "Beneficial Owner City",
  usAddressRegion: "Beneficial Owner State",
  addressRegion: "Beneficial Owner Region",
  usPostalCode: "Beneficial Owner ZIP Code",
  postalCode: "Beneficial Owner Postal Code",
};

/**
 * Builds the entity profile inputs, switching between the
 * disregarded-entity and the regular-entity beneficial-owner fields based
 * on the profile's current answers.
 */
const getProfileEntityOtherSchema = (
  organization: Organization,
  updateSchema: UpdateSchema,
  profileNameExtraWarning: string | null,
  profile: FormProfileEntity = {},
): Input[] => {
  const { email: organizationEmail } = organization;

  const entityNoEinAlert = getNoEinAlert(organizationEmail);

  const {
    entityType,
    hasEinNumber,
    beneficialOwnerAddress,
    beneficialOwnerEntityType,
    isDisregardedForTaxPurposes,
    hasSsn,
    isUsPerson,
    isUsCitizen,
  } = profile;

  const inputPropsMap = getProfileEntityInputsMap();

  const isIndividual = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_INDIVIDUAL;
  const isIra = beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;
  const isSetBeneficialType = beneficialOwnerEntityType !== undefined;
  const isNotDisregarded = isDisregardedForTaxPurposes === false;
  const isNotUsPerson = isUsPerson === false;

  if (isNotDisregarded) {
    profile.beneficialOwnerEntityType = undefined;
  }

  if (isDisregardedForTaxPurposes) {
    profile.entityType = undefined;
  }

  const isLLC =
    (isDisregardedForTaxPurposes && profile.beneficialOwnerEntityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_LLC) ||
    (isNotDisregarded && profile.entityType === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_LLC);

  const commonInputs: Input[] = [
    {
      name: "name",
      type: TYPE_STRING,
      label: "Entity’s Full Legal Name",
      required: true,
      maxLength: 255,
      itemProps: {
        extra: profileNameExtraWarning
          ? (
            <MarkdownAlert
              text={profileNameExtraWarning}
              type="warning"
              style={WARNING_STYLE}
            />
          )
          : null,
        rules: [
          RULES.ENTITY_NAME,
        ],
      },
      inputProps: {
        /** Refreshes the schema after the entity name changes. */
        onChange: () => updateSchema(),
      },
    },
    {
      name: "title",
      type: TYPE_STRING,
      label: "Your Title",
      required: true,
    },
    {
      name: "isDisregardedForTaxPurposes",
      label: "Is this entity disregarded for federal income tax purposes?",
      required: true,
      itemProps: {
        extra: <Disclaimer text={TOOLTIP_PROFILE_DRE} />,
      },
      inputProps: {
        /** Refreshes the schema after the disregarded-entity answer changes. */
        onChange: () => updateSchema(),
      },
    },
  ];

  const defaultHiddenInputs: Input[] = [
    inputPropsMap.beneficialOwnerEntityType,
    inputPropsMap.beneficialOwnerName,
    inputPropsMap.beneficialOwnerAddress,
    inputPropsMap.isUsCitizen,
    inputPropsMap.beneficialOwnerSsnNumber,
    inputPropsMap.beneficialOwnerEinNumber,
  ];

  const beneficialOwnerTooltip = isDisregardedForTaxPurposes === true
    ? TOOLTIP_DRE_BENEFICIAL_OWNER_NAME
    : TOOLTIP_BENEFICIAL_OWNER_NAME;

  let inputs: Input[] = [
    ...commonInputs,
    ...defaultHiddenInputs,
    inputPropsMap.entityType,
    inputPropsMap.address,
    inputPropsMap.einNumber,
    inputPropsMap.residenceState,
    inputPropsMap.hasOtherUsResidenceState,
    inputPropsMap.isUsPerson,
    inputPropsMap.hasSsn,
    inputPropsMap.hasEinNumber,
    inputPropsMap.isRetirementPlan,
    inputPropsMap.beneficialOwnerCustodianEinNumber,
    inputPropsMap.beneficialOwnerTaxFilingStatus,
    inputPropsMap.taxFilingStatus,
  ];

  const isNotUsCitizen = isUsCitizen === false;
  const isTin = isNotUsPerson && isNotUsCitizen;

  /** Returns the has-SSN or has-EIN label for the current beneficial owner type. */
  const getHasSsnLabel = () => {
    if (isIndividual) {
      if (isTin) {
        return "Does this beneficial owner have a US TIN?";
      }

      return "Does this beneficial owner have a SSN?";
    }

    if (isIra) {
      return "Does this beneficial owner custodian have an EIN?";
    }

    return "Does this beneficial owner have an EIN?";
  };

  let labels: ResidencyLabels = {
    hasOtherUsResidenceState: "Is the state listed under the address also the beneficial owner's state of residence for tax purposes?",
    residenceState: "Please list the beneficial owner's state of residence",
    isUsPerson: LABEL_DISREGARDED_IS_US_PERSON,
    isUsPersonTooltip: TOOLTIP_DISREGARDED_IS_US_PERSON,
    hasSsn: getHasSsnLabel(),
  };

  const noEinAlert = hasEinNumber === false
    ? (
      <Alert
        showIcon
        type="warning"
        style={ALERT_STYLE}
        message={TITLE_WARNING}
        description={<Markdown text={entityNoEinAlert} />}
      />
    )
    : undefined;

  if (isDisregardedForTaxPurposes) {
    const einNumberInput: Input = hasEinNumber
      ? {
        name: "einNumber",
        type: TYPE_EIN,
        label: LABEL_ENTITY_EIN,
        required: true,
      }
      : inputPropsMap.einNumber;

    if (isSetBeneficialType && !isIndividual) {
      profile.isUsCitizen = false;
    }

    const llcInputs: Input[] = isLLC
      ? [
        {
          name: "beneficialOwnerTaxFilingStatus",
          type: TYPE_RADIO,
          label: LABEL_TAX_FILING_STATUS,
          required: true,
          options: Object.keys(BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP)
            .map((value) => ({ value, label: BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP[value] })),
        },
      ]
      : [
        inputPropsMap.beneficialOwnerTaxFilingStatus,
      ];

    const iraInputs: Input = isIra && hasSsn
      ? {
        name: "beneficialOwnerCustodianEinNumber",
        type: TYPE_EIN,
        label: "Beneficial Owner Custodian EIN",
      }
      : inputPropsMap.beneficialOwnerCustodianEinNumber;

    const addressInputs: Input = isSetBeneficialType
      ? {
        name: "beneficialOwnerAddress",
        label: "",
        schema: getAddressSchema(updateSchema, [ "profileEntity", "beneficialOwnerAddress" ], beneficialOwnerAddress, {}, BENEFICIAL_OWNER_ADDRESS_LABELS),
      }
      : inputPropsMap.beneficialOwnerAddress;

    const isUsCitizenInput: Input = isIndividual
      ? {
        name: "isUsCitizen",
        label: "Is this beneficial owner a US citizen?",
        required: true,
        inputProps: {
          /** Refreshes the schema after the citizenship answer changes. */
          onChange: () => updateSchema(),
        },
      }
      : inputPropsMap.isUsCitizen;

    const residencyInputs = computeResidencyInputs(profile, updateSchema, labels, organizationEmail);

    const stateInputs: Input[] = isSetBeneficialType
      ? [
        ...residencyInputs,
      ]
      : [
        inputPropsMap.residenceState,
        inputPropsMap.hasOtherUsResidenceState,
        inputPropsMap.isUsPerson,
        inputPropsMap.hasSsn,
      ];

    const shouldShowSnnInput = isIndividual && hasSsn;

    const shouldShowEinInput =
      isSetBeneficialType &&
      !isIndividual &&
      hasSsn &&
      !isIra;

    const einInput: Input = shouldShowEinInput
      ? {
        name: "beneficialOwnerEinNumber",
        type: TYPE_EIN,
        required: true,
        label: "Beneficial Owner EIN",
      }
      : inputPropsMap.beneficialOwnerEinNumber;

    const ssnInput: Input = shouldShowSnnInput
      ? {
        name: "beneficialOwnerSsnNumber",
        required: true,
        label: isTin ? "Beneficial Owner ITIN" : "Beneficial Owner SSN",
      }
      : inputPropsMap.beneficialOwnerSsnNumber;

    /**
     * Refreshes the schema after the beneficial owner's entity type
     * changes, warning when it is an IRA that must go through the
     * SDIRA flow instead.
     */
    const onBeneficialOwnerEntityTypeChange = (event: RadioChangeEvent) => {
      const { value } = event.target;
      const isDisregardedIRA = value === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;

      if (isDisregardedIRA) {
        return updateSchema(DISCARDED_IRA_ALERT, "beneficialOwnerEntityType");
      }

      return updateSchema();
    };

    inputs = [
      ...commonInputs,
      ...[
        inputPropsMap.address,
        inputPropsMap.entityType,
        inputPropsMap.isRetirementPlan,
        inputPropsMap.taxFilingStatus,
        {
          name: "hasEinNumber",
          label: LABEL_HAS_EIN,
          required: true,
          itemProps: {
            extra: noEinAlert,
          },
          inputProps: {
            /** Refreshes the schema after the has-EIN answer changes. */
            onChange: () => updateSchema(),
          },
        },
        einNumberInput,
        {
          name: "beneficialOwnerEntityType",
          type: TYPE_RADIO,
          label: "Beneficial Owner Entity Type",
          required: true,
          options: Object
            .keys(ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP)
            .map((value) => ({ value, label: ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_TITLES_MAP[value] })),
          itemProps: {
            rules: [
              {
                /** Rejects an IRA beneficial owner entity type. */
                validator: async (_: unknown, value: string) => {
                  const isIRA = value === ENTITY_BENEFICIAL_OWNER_ENTITY_TYPE_IRA;

                  if (isIRA) {
                    throw new Error(INVALID_BENEFICIAL_OWNER_ENTITY_TYPE_LABEL);
                  }
                },
              },
            ],
          },
          inputProps: {
            onChange: onBeneficialOwnerEntityTypeChange,
          },
        },
        {
          name: "beneficialOwnerName",
          label: LABEL_BENEFICIAL_OWNER_NAME,
          type: TYPE_STRING,
          itemProps: {
            extra: <Disclaimer text={beneficialOwnerTooltip} />,
          },
          required: true,
        },
        ...llcInputs,
        addressInputs,
        isUsCitizenInput,
      ],
      ...stateInputs,
      ...[
        einInput,
        ssnInput,
        iraInputs,
      ],
      inputPropsMap.taxFilingStatus,
    ];
  }

  if (isNotDisregarded) {
    profile.isUsCitizen = false;
    const llcInputs: Input[] = isLLC
      ? [
        {
          name: "taxFilingStatus",
          type: TYPE_RADIO,
          label: LABEL_TAX_FILING_STATUS,
          required: true,
          options: Object.keys(BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP)
            .map((value) => ({ value, label: BENEFICIAL_OWNER_TAX_FILING_STATUS_TITLES_MAP[value] })),
        },
      ]
      : [
        inputPropsMap.taxFilingStatus,
      ];

    labels = {
      hasOtherUsResidenceState: "Is the state listed under the address also the entity's state of residence for tax purposes?",
      residenceState: "Please list the entity's state of residence",
      isUsPerson: "Is this entity considered a US person for federal tax purposes, as defined by the IRS?",
    };

    const isSetEntityType = entityType !== undefined;

    const residencyInputs = computeResidencyInputs(profile, updateSchema, labels, organizationEmail);

    const shouldShowEinInput = profile.hasEinNumber === true;
    const einNumberInput: Input = shouldShowEinInput
      ? {
        name: "einNumber",
        type: TYPE_EIN,
        label: LABEL_ENTITY_EIN,
        required: true,
      }
      : inputPropsMap.einNumber;

    const stateInputs: Input[] = isSetEntityType
      ? [
        ...residencyInputs,
        inputPropsMap.hasSsn,
      ]
      : [
        inputPropsMap.residenceState,
        inputPropsMap.hasOtherUsResidenceState,
        inputPropsMap.isUsPerson,
        inputPropsMap.hasSsn,
      ];

    const addressInputs: Input = isSetEntityType
      ? {
        name: "address",
        label: "",
        schema: getUsAddressSchema(beneficialOwnerAddress),
        required: true,
      }
      : inputPropsMap.address;

    // NOTE: the retirement plan question is not asked; the input stays
    //       hidden for every entity type.
    const isRetirementPlanInput = inputPropsMap.isRetirementPlan;

    /**
     * Refreshes the schema after the entity type changes, warning when it
     * is an IRA that must go through the SDIRA flow instead.
     */
    const onEntityTypeChange = (event: RadioChangeEvent) => {
      const { value } = event.target;
      const isIRA = value === ENTITY_TYPE_IRA;

      if (isIRA) {
        return updateSchema(DISCARDED_IRA_ALERT, "entityType");
      }

      return updateSchema();
    };

    inputs = [
      ...commonInputs,
      ...defaultHiddenInputs,
      inputPropsMap.beneficialOwnerTaxFilingStatus,
      ...[{
        name: "entityType",
        type: TYPE_RADIO,
        label: "Entity Type",
        required: true,
        options: Object
          .keys(ENTITY_TYPE_TITLES_MAP)
          .map((value) => ({ value, label: ENTITY_TYPE_TITLES_MAP[value] })),
        itemProps: {
          rules: [
            {
              /** Rejects an IRA entity type. */
              validator: async (_: unknown, value: string) => {
                const isIRA = value === ENTITY_TYPE_IRA;

                if (isIRA) {
                  throw new Error(INVALID_ENTITY_TYPE_LABEL);
                }
              },
            },
          ],
        },
        inputProps: {
          onChange: onEntityTypeChange,
        },
      }],
      ...llcInputs,
      ...[
        isRetirementPlanInput,
        addressInputs,
      ],
      ...stateInputs,
      {
        name: "hasEinNumber",
        label: LABEL_HAS_EIN,
        required: true,
        itemProps: {
          extra: noEinAlert,
        },
        inputProps: {
          /** Refreshes the schema after the has-EIN answer changes. */
          onChange: () => updateSchema(),
        },
      },
      ...[ einNumberInput ],
      inputPropsMap.beneficialOwnerCustodianEinNumber,
    ];
  }

  return inputs;
};

export default getProfileEntityOtherSchema;
